import { ISelectOption } from '@aon/aon-tws-common-ui';
import _ from 'lodash';
import { ElementType, IPageFlowElementModel, ISurveyElement } from '../models';
import { defaultCulture, TranslationHelper } from './';

const ResequenceElements = (existingElements: ISurveyElement[], addedUpdatedElements: ISurveyElement[]): ISurveyElement[] => {
	let originalSequence: number | undefined;
	let updatedElements: ISurveyElement[] = [...existingElements];

	addedUpdatedElements.forEach((pageFlowElement) => {
		originalSequence = existingElements.find((e) => e.id === pageFlowElement.id)?.sequence;

		if (addedUpdatedElements.length > 1 && originalSequence === undefined) {
			const priorExistingElements = updatedElements.filter((element) => {
				if (element.sequence !== undefined && pageFlowElement.sequence !== undefined && SurveyDesignHelper.IsPageFlowElement(element)) {
					return element.sequence <= pageFlowElement.sequence;
				}
			}).length;
			pageFlowElement.sequence =
				pageFlowElement.sequence !== undefined ? pageFlowElement.sequence + priorExistingElements : priorExistingElements;
		}

		updatedElements = updateElementSequences(updatedElements, originalSequence, pageFlowElement);
	});

	return originalSequence ? updatedElements : updatedElements.concat(addedUpdatedElements);
};

const updateElementSequences = (
	elements: ISurveyElement[],
	originalSequence: number | undefined,
	addedUpdatedElement: ISurveyElement,
): ISurveyElement[] => {
	return elements.map((e) => {
		// If we have an originalSequence, then
		// we are editing an element instead of adding one.
		if (originalSequence) {
			if (e.id === addedUpdatedElement.id) {
				return { ...addedUpdatedElement };
			}

			if (originalSequence > addedUpdatedElement.sequence!) {
				// we are moving the element up towards the top of the list
				if (e.sequence! <= originalSequence && e.sequence! >= addedUpdatedElement.sequence!) {
					return { ...e, sequence: e.sequence! + 1 };
				}
			} else {
				// we are moving the element down towards the bottom of the list
				if (e.sequence! >= originalSequence && e.sequence! <= addedUpdatedElement.sequence!) {
					return { ...e, sequence: e.sequence! - 1 };
				}
			}
		} else if (e.sequence! >= addedUpdatedElement.sequence!) {
			return { ...e, sequence: e.sequence! + 1 };
		}

		return { ...e };
	});
};

const AddNewElement = (
	maxId: number,
	elementType: ElementType,
	defaultCultureCode?: string,
	availableCultures?: ISelectOption[],
	supportedCultures?: string[],
): IPageFlowElementModel => {
	const availableDefaultCulture = _.find(availableCultures, (c) => c.name === defaultCultureCode);
	const newElement: IPageFlowElementModel = {
		id: maxId + 1,
		elementType,
		translations: [],
		text: TranslationHelper.initializeEditItemForCultureCode({
			...defaultCulture,
			cultureCode: defaultCultureCode!,
			displayName: availableDefaultCulture?.displayText,
		}),
		isRandomized: false,
	};
	if (supportedCultures) {
		newElement.translations = TranslationHelper.initializeEditableTranslationItems(
			defaultCultureCode!,
			availableCultures!,
			supportedCultures,
		);
	}
	return newElement;
};

const UpdateExistingElement = (element: IPageFlowElementModel, beforeElementSequence: number, originalSequence?: number) => {
	const beforeAdjuster = originalSequence! < beforeElementSequence ? 1 : 0;
	element.sequence = beforeElementSequence - beforeAdjuster;
	return element;
};

const IsPageFlowElement = (element: ISurveyElement) => {
	return (
		element.elementType === ElementType.PageBreak ||
		element.elementType === ElementType.SectionBreak ||
		element.elementType === ElementType.SectionHeader ||
		element.elementType === ElementType.TextElement
	);
};

const SetRandomizationForNewPageBreaks = (
	elements: ISurveyElement[],
	addedUpdatedIds: number[],
	isFirstPageRandomized: boolean,
): ISurveyElement[] => {
	const sortedElements = [...elements].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));

	let shouldBeRandomized = isFirstPageRandomized;
	return sortedElements.map((e) => {
		if (e.elementType === ElementType.PageBreak) {
			if (addedUpdatedIds.indexOf(e.id) > -1) {
				return { ...e, isRandomized: shouldBeRandomized };
			} else {
				shouldBeRandomized = e.isRandomized || false;
				return { ...e };
			}
		} else {
			return { ...e };
		}
	});
};

export const SurveyDesignHelper = {
	ResequenceElements,
	UpdateExistingElement,
	AddNewElement,
	IsPageFlowElement,
	SetRandomizationForNewPageBreaks,
};
